/**
 * Auth Interceptor Hook
 *
 * Global error handling for API responses.
 * Intercepts 401 errors and logs out user.
 * Displays error toasts for API failures.
 */

import { useEffect } from 'react';
import { toast } from 'react-toastify';
import { useAuthActions } from './useAuthHooks';
import { is401Error, getErrorMessage } from '../api/apiErrorHandler';

/**
 * useAuthInterceptor - Global API error handler
 *
 * Features:
 * - Listens for RTK Query errors via RTK listener
 * - Handles 401 (Unauthorized) - logs out user
 * - Shows error toasts for API failures
 * - Integrates with Redux for state changes
 *
 * Usage:
 * ```
 * function App() {
 *   useAuthInterceptor();
 *   return <AppRoutes />;
 * }
 * ```
 *
 * Note: This hook relies on RTK Query error handling.
 * Errors are caught at component level when mutation/query fails.
 * For global error handling, we use Redux middleware (future enhancement).
 */
export const useAuthInterceptor = (): void => {
  const { logout } = useAuthActions();

  useEffect(() => {
    /**
     * Note: Full API-wide interceptor requires Redux middleware.
     * Current implementation provides:
     * 1. logout() action available globally via useAuthActions
     * 2. Error handling at component level (useLoginMutation hook)
     * 3. RTK Query middleware for request/response logging
     *
     * Future enhancement: Add RTK Middleware listener for global 401 handling
     * to automatically logout without requiring component-level handling.
     */
  }, [logout]);
};

/**
 * Helper hook for individual components to handle API errors
 *
 * @param error - RTK Query error from mutation/query
 * @param customErrorHandler - Optional custom error handler function
 *
 * Usage:
 * ```
 * const { handleApiError } = useApiErrorHandler();
 *
 * try {
 *   await login(credentials).unwrap();
 * } catch (err) {
 *   handleApiError(err);
 * }
 * ```
 */
export const useApiErrorHandler = () => {
  const { logout } = useAuthActions();

  const handleApiError = (error: unknown, customErrorHandler?: (msg: string) => void) => {
    // Check for 401 Unauthorized
    if (is401Error(error)) {
      logout();
      toast.error('Session expired. Please log in again.', {
        autoClose: 5000,
        position: 'top-right',
      });
      return;
    }

    // Extract and display error message
    const errorMessage = getErrorMessage(error);

    // Call custom handler if provided
    if (customErrorHandler) {
      customErrorHandler(errorMessage);
    } else {
      // Default: show error toast
      toast.error(errorMessage, {
        autoClose: 5000,
        position: 'top-right',
      });
    }
  };

  return { handleApiError };
};
